/**
 * Contratto KPI snapshot Home (PR-KPI-SNAPSHOT).
 *
 * Fonte unica delle tre derivazioni snapshot-tate (pazienti attivi,
 * da ricontattare, compleanni) e dello schema documento Firestore.
 * Consumato dal client (`src/app/services/dashboard.ts` re-esporta le
 * derivazioni) e dalle Cloud Functions (mirror generato in
 * `functions/src/shared/kpi-snapshot.ts`).
 *
 * VINCOLO ZERO-IMPORT: questo modulo non importa nulla. Nessun firebase,
 * nessun side effect, nessun import relativo. Deve compilare identico
 * sotto tsconfig.app (bundler) e tsconfig functions (NodeNext). Le tre
 * helper data usate da deriveCompleanni sono copiate da
 * `src/shared/utils/date.ts` (getRomeDateStr, addDaysToDateStr,
 * isBirthdayThisWeek): non e' un secondo SSOT voluto, e' l'unica forma
 * compatibile col vincolo. Vedi docs/kpi-snapshot-stop-log.md Dubbi.
 *
 * Le derivazioni sono LOGICAMENTE IDENTICHE a quelle in
 * src/app/services/dashboard.ts (pre-delega). Non introdurre soglia 60gg,
 * non cambiare il cap 20, non cappare i compleanni, non toccare 29/02.
 */

// ---------------------------------------------------------------------
// Path e costanti documento
// ---------------------------------------------------------------------

/** Collection root degli snapshot KPI. Scritta solo da Cloud Function. */
export const KPI_SNAPSHOT_COLLECTION = 'kpi_snapshot';

/** Id del documento studio-wide (TITOLARE / SEGRETERIA). */
export const KPI_SNAPSHOT_STUDIO_DOC_ID = '_studio';

/** Versione schema documento. Incrementare se i campi cambiano. */
export const KPI_SNAPSHOT_SCHEMA_VERSION = 1;

/** Soglia staleness (Q3): oltre 36h lo snapshot e' «non aggiornato». */
export const KPI_SNAPSHOT_STALE_AFTER_MS = 36 * 60 * 60 * 1000;

/** Cap lista «da ricontattare» (invariante UI / deriveDaRicontattare). */
export const DA_RICONTATTARE_MAX = 20;

// ---------------------------------------------------------------------
// Schema documento
// ---------------------------------------------------------------------

/**
 * Documento `/kpi_snapshot/{scopeId}`.
 *
 * `scopeId` = `_studio` oppure uid della dietologa.
 * Solo `pazienteId` nelle liste (Q7): niente nome, CF, misure, esami.
 * `generatedAt` e' un Timestamp Firestore (serverTimestamp in write);
 * tipizzato in modo strutturale per restare zero-import.
 */
export interface KpiSnapshotDoc {
  schemaVersion: number;
  /** `_studio` oppure uid dietologa. */
  scopeId: string;
  /** Timestamp Firestore. Null solo in transito prima del commit server. */
  generatedAt: { seconds: number; nanoseconds: number } | null;
  /** ISO 8601 UTC del clock di generazione (leggibile senza SDK). */
  generatedAtIso: string;
  /** Epoch ms del clock di generazione, per il confronto 36h. */
  generatedAtMillis: number;
  pazientiAttiviCount: number;
  /** Conteggio uncapped (prima del cap 20). */
  daRicontattareTotal: number;
  /** Prime `DA_RICONTATTARE_MAX` voci, ordine cognome Query B. */
  daRicontattareIds: string[];
  /** Uguale a `compleanniIds.length` (nessun cap). */
  compleanniTotal: number;
  compleanniIds: string[];
}

// ---------------------------------------------------------------------
// Tipi strutturali delle derivazioni (no import da @shared/types)
// ---------------------------------------------------------------------

export interface KpiPazienteRef {
  id: string;
  stato: string;
  dataNascita?: Date | string | null;
}

export interface KpiAppuntamentoRef {
  pazienteId: string;
  data: Date | string;
}

export interface KpiAppuntamentoStatoRef {
  stato?: string;
}

// ---------------------------------------------------------------------
// Helper data (copia da src/shared/utils/date.ts — non esportate)
// ---------------------------------------------------------------------

/** Copia di date.ts:46-48. */
function getRomeDateStr(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Rome' }).format(date);
}

/** Copia di date.ts:78-81. */
function addDaysToDateStr(dateStr: string, n: number): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(y, mo - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Copia di date.ts:122-138.
 * Confronto giorno/mese, ignora l'anno.
 * I pazienti senza dataNascita o con formato non "YYYY-MM-DD" vengono esclusi.
 * I nati il 29/02 non compaiono negli anni non bisestili (comportamento voluto).
 */
function isBirthdayThisWeek(
  dataNascita: Date | string | undefined | null,
  weekRange: { start: Date; end: Date },
): boolean {
  if (!dataNascita) return false;
  const raw =
    dataNascita instanceof Date
      ? dataNascita.toISOString().slice(0, 10)
      : String(dataNascita).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return false;
  const birthMD = raw.slice(5); // "MM-DD"
  const monday = getRomeDateStr(weekRange.start);
  for (let i = 0; i < 7; i++) {
    if (addDaysToDateStr(monday, i).slice(5) === birthMD) return true;
  }
  return false;
}

// ---------------------------------------------------------------------
// Derivazioni (identiche a dashboard.ts pre-delega)
// ---------------------------------------------------------------------

/**
 * Conta i pazienti "attivi" (D1):
 *  - paziente.stato !== 'archiviato'
 *  - almeno un appuntamento negli ultimi 12 mesi (passato, non futuro)
 *
 * Un appuntamento solo-futuro NON rende il paziente attivo.
 * Gli appuntamenti in `appuntamenti` provengono dalla finestra [12mFA, ∞)
 * di Query A; vengono filtrati a < now per ottenere solo quelli passati.
 */
export function derivePazientiAttivi(
  pazienti: ReadonlyArray<Pick<KpiPazienteRef, 'id' | 'stato'>>,
  appuntamenti: ReadonlyArray<KpiAppuntamentoRef>,
  now: Date,
): number {
  const nowIso = now.toISOString();
  const conAppPassato = new Set(
    appuntamenti
      .filter((a) => String(a.data) < nowIso)
      .map((a) => a.pazienteId),
  );
  return pazienti.filter(
    (p) => p.stato !== 'archiviato' && conAppPassato.has(p.id),
  ).length;
}

/**
 * Deriva i pazienti "da ricontattare" (D2):
 *  - nessun appuntamento futuro in stato 'programmato'
 *  - il loro ultimo appuntamento passato è in stato 'completato'
 *
 * NESSUNA soglia temporale — non viene applicato nessun limite temporale.
 * Limitato a `max` risultati (default 20) per UX.
 *
 * Il totale uncapped (campo `daRicontattareTotal` dello snapshot) si ottiene
 * chiamando con `max = pazienti.length`.
 */
export function deriveDaRicontattare<T extends { id: string }>(
  pazienti: ReadonlyArray<T>,
  conFuturoProgrammato: ReadonlySet<string>,
  ultimoPassato: ReadonlyMap<string, KpiAppuntamentoStatoRef>,
  max = DA_RICONTATTARE_MAX,
): T[] {
  return pazienti
    .filter((p) => {
      if (conFuturoProgrammato.has(p.id)) return false;
      const ultimo = ultimoPassato.get(p.id);
      return ultimo?.stato === 'completato';
    })
    .slice(0, max);
}

/**
 * Filtra i pazienti il cui compleanno cade nella settimana corrente.
 */
export function deriveCompleanni<T extends { dataNascita?: Date | string | null }>(
  pazienti: ReadonlyArray<T>,
  weekRange: { start: Date; end: Date },
): T[] {
  return pazienti.filter((p) => isBirthdayThisWeek(p.dataNascita, weekRange));
}
